import React from "react";
import CodeBlock from "../components/CodeBlock";

const McpPlatformGuides = () => (
  <section id="mcp-platforms" className="developer-section">
    <h2>Platform Guides: Claude, ChatGPT, and Gemini</h2>
    <p>
      Once your Plex MCP server is running, connect it to your AI assistant of choice.
      Each platform has its own configuration format.
    </p>
    <p>
      <a href="https://github.com/plex-research/plex-mcp" target="_blank" rel="noopener noreferrer">
        github.com/plex-research/plex-mcp
      </a>{" "}
      has MCP configuration references, LLM integration guides, and pre-packaged
      commands/plugins for each platform below.
    </p>

    <h3>Claude Desktop &amp; Claude Code</h3>
    <p>
      Claude supports remote MCP servers natively &mdash; no local process to run. Point either
      client at the hosted Plex endpoint with your API key.
    </p>

    <h4>Claude Desktop</h4>
    <p>
      Edit <code>~/Library/Application Support/Claude/claude_desktop_config.json</code> (macOS)
      or <code>%APPDATA%\Claude\claude_desktop_config.json</code> (Windows):
    </p>
    <CodeBlock
      title="claude_desktop_config.json"
      examples={{
        json: `{
  "mcpServers": {
    "plex": {
      "url": "https://plexsearch.com/mcp",
      "headers": {
        "x-api-key": "<your-api-key>"
      }
    }
  }
}`,
      }}
    />

    <h4>Claude Code</h4>
    <p>
      <code>claude mcp add --transport http plex https://plexsearch.com/mcp --header "x-api-key: &lt;your-api-key&gt;"</code>
    </p>
    <p>Or add the Plex Search plugin, which bundles this setup; see the{" "}
      <a href="https://github.com/plex-research/plex-mcp" target="_blank" rel="noopener noreferrer">
        plex-mcp
      </a> README.
    </p>

    <h4>Verify it works</h4>
    <p>After restarting Claude, you should see the Plex tools available. Try asking:</p>
    <div className="dev-example-prompt">
      <p><em>"Search for compounds related to aspirin and tell me about their targets."</em></p>
    </div>
    <p>
      Claude will call the <code>resolve</code> tool to find aspirin's ID, then <code>search_analyst</code>{" "}
      or <code>guide_agent</code> to find related compounds and targets, and synthesize the results
      into a natural language response.
    </p>

    <h3>ChatGPT (Custom GPTs / Actions)</h3>
    <p>
      ChatGPT connects to external APIs through <strong>Actions</strong> in Custom GPTs.
      Define the Plex API as an OpenAPI schema:
    </p>
    <CodeBlock
      title="OpenAPI schema for ChatGPT Actions"
      examples={{
        yaml: `openapi: 3.1.0
info:
  title: Plex Search API
  description: Search the Plex biomedical knowledge graph
  version: "1.0"
servers:
  - url: https://plexsearch.com

paths:
  /api/suggestions:
    post:
      operationId: resolveEntities
      summary: Resolve entity names to Plex IDs
      requestBody:
        required: true
        content:
          application/json:
            schema:
              type: object
              properties:
                terms:
                  type: array
                  items:
                    type: string
                  description: Entity names to resolve
              required: [terms]
      responses:
        "200":
          description: Matching entities with confidence scores

  /api/search:
    post:
      operationId: searchKnowledgeGraph
      summary: Search for related entities in the knowledge graph
      requestBody:
        required: true
        content:
          application/json:
            schema:
              type: object
              properties:
                ids:
                  type: array
                  items:
                    type: string
                  description: Entity IDs to search
                sim_threshold:
                  type: number
                  description: Minimum similarity (0.0-1.0, default 0.75)
                page_size:
                  type: integer
                  description: Max results per category
              required: [ids]
      responses:
        "200":
          description: Search results grouped by category

  /api/members/{id}:
    get:
      operationId: getEntityMembers
      summary: Get members of an entity set
      parameters:
        - name: id
          in: path
          required: true
          schema:
            type: string
      responses:
        "200":
          description: Member entities`,
      }}
    />
    <p>
      In the ChatGPT Custom GPT editor, paste this schema under <strong>Actions</strong>.
      Set the authentication to "API Key" with the header <code>x-api-key: &#123;key&#125;</code>.
    </p>

    <h3>Google Gemini</h3>
    <p>
      Gemini supports function calling through its API. Define the Plex tools
      as Gemini function declarations:
    </p>
    <CodeBlock
      title="Gemini function declarations"
      examples={{
        python: `import google.generativeai as genai

# Define Plex tools as Gemini functions
plex_tools = [
    genai.protos.Tool(
        function_declarations=[
            genai.protos.FunctionDeclaration(
                name="search",
                description="Search the Plex biomedical knowledge graph for entities related to the given IDs",
                parameters=genai.protos.Schema(
                    type=genai.protos.Type.OBJECT,
                    properties={
                        "ids": genai.protos.Schema(
                            type=genai.protos.Type.ARRAY,
                            items=genai.protos.Schema(type=genai.protos.Type.STRING),
                            description="Entity IDs to search"
                        ),
                        "sim_threshold": genai.protos.Schema(
                            type=genai.protos.Type.NUMBER,
                            description="Minimum similarity 0.0-1.0"
                        ),
                    },
                    required=["ids"],
                ),
            ),
            genai.protos.FunctionDeclaration(
                name="resolve",
                description="Resolve entity names to Plex IDs",
                parameters=genai.protos.Schema(
                    type=genai.protos.Type.OBJECT,
                    properties={
                        "terms": genai.protos.Schema(
                            type=genai.protos.Type.ARRAY,
                            items=genai.protos.Schema(type=genai.protos.Type.STRING),
                            description="Names to resolve"
                        ),
                    },
                    required=["terms"],
                ),
            ),
        ]
    )
]

model = genai.GenerativeModel("gemini-2.0-flash", tools=plex_tools)
chat = model.start_chat()

response = chat.send_message("Find compounds similar to aspirin and their targets")

# Handle function calls
for part in response.parts:
    if fn := part.function_call:
        print(f"Gemini wants to call: {fn.name}({dict(fn.args)})")
        # Execute against Plex API and return results
        result = call_plex_api(fn.name, dict(fn.args))
        response = chat.send_message(
            genai.protos.Content(
                parts=[genai.protos.Part(
                    function_response=genai.protos.FunctionResponse(
                        name=fn.name,
                        response={"result": result}
                    )
                )]
            )
        )
        print(response.text)`,
        javascript: `import { GoogleGenerativeAI } from "@google/generative-ai";

const genAI = new GoogleGenerativeAI(process.env.GEMINI_API_KEY);

const plexTools = [{
  functionDeclarations: [
    {
      name: "search",
      description: "Search the Plex biomedical knowledge graph",
      parameters: {
        type: "OBJECT",
        properties: {
          ids: { type: "ARRAY", items: { type: "STRING" }, description: "Entity IDs" },
          sim_threshold: { type: "NUMBER", description: "Min similarity 0.0-1.0" },
        },
        required: ["ids"],
      },
    },
    {
      name: "resolve",
      description: "Resolve entity names to Plex IDs",
      parameters: {
        type: "OBJECT",
        properties: {
          terms: { type: "ARRAY", items: { type: "STRING" }, description: "Names to resolve" },
        },
        required: ["terms"],
      },
    },
  ],
}];

const model = genAI.getGenerativeModel({
  model: "gemini-2.0-flash",
  tools: plexTools,
});

const chat = model.startChat();
const result = await chat.sendMessage("Find compounds similar to aspirin");

// Handle function calls from Gemini
for (const part of result.response.candidates[0].content.parts) {
  if (part.functionCall) {
    const { name, args } = part.functionCall;
    console.log(\`Gemini calls: \${name}(\${JSON.stringify(args)})\`);
    const apiResult = await callPlexApi(name, args);
    // Send function response back to Gemini
    const followUp = await chat.sendMessage([{
      functionResponse: { name, response: { result: apiResult } }
    }]);
    console.log(followUp.response.text());
  }
}`,
      }}
    />

    <h3>Testing Your Integration</h3>
    <p>Here are some example prompts to verify your MCP integration is working:</p>
    <div className="dev-example-prompts">
      <div className="dev-example-prompt">
        <strong>Basic search:</strong>
        <p><em>"What are the known targets of ibuprofen?"</em></p>
      </div>
      <div className="dev-example-prompt">
        <strong>Multi-entity query:</strong>
        <p><em>"Compare the target profiles of aspirin and acetaminophen."</em></p>
      </div>
      <div className="dev-example-prompt">
        <strong>Exploratory:</strong>
        <p><em>"Find compounds that interact with EGFR and show me related diseases."</em></p>
      </div>
      <div className="dev-example-prompt">
        <strong>Literature:</strong>
        <p><em>"What recent publications discuss the relationship between metformin and cancer?"</em></p>
      </div>
    </div>
  </section>
);

export default McpPlatformGuides;
